
import { RichTextEditor } from "@/components/email-builder/RichTextEditor";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TextBlockConfiguration } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  LinkControls,
  ResponsiveControls,
} from "../shared";

interface TextBlockPropertiesProps {
  config: TextBlockConfiguration;
  onChange: (config: TextBlockConfiguration) => void;
}

/**
 * TextBlockProperties Component
 * 
 * Property panel for text blocks with rich text editing and formatting controls.
 * 
 * Sections:
 * - Content (always visible) - Rich text editor
 * - Typography - Font, size, weight, line height, color
 * - Alignment - Horizontal and vertical alignment
 * - Spacing - Padding controls
 * - Link - Optional link configuration
 * - Responsive - Mobile/desktop visibility
 * 
 * @param config - Current text block configuration
 * @param onChange - Callback when configuration changes
 */
export function TextBlockProperties({ config, onChange }: TextBlockPropertiesProps) {
  const updateField = <K extends keyof TextBlockConfiguration>(
    field: K,
    value: TextBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["typography", "alignment"]} className="w-full">
        
        {/* Typography Section */}
        <AccordionItem value="typography">
          <AccordionTrigger>Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.typography}
              onChange={(typography) => updateField('typography', typography)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Alignment Section */}
        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Spacing Section */}
        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Link Section */}
        <AccordionItem value="link">
          <AccordionTrigger>Link</AccordionTrigger>
          <AccordionContent>
            <LinkControls
              value={config.link}
              onChange={(link) => updateField('link', link)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Responsive Section */}
        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
